<template>
  <div class="mypage-wrapper">
    <header-pane
      :hasHeaderNav="hasHeaderNav"
      :pageTitle="$tc('MypagePage.pageTitle')"
      colorType="light"
    />
    <router-view :enabledListCarousel="false" />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, onBeforeMount, onUnmounted, watch } from '@vue/composition-api'
import VueRouter from 'vue-router'
import HeaderPane from '@/components/common/Header/HeaderPane.vue'
import usePusherContractInfo from '@/components/hook/usePusherContractInfo'
import useRealtimeMessaging, { RTMCallbackParamType } from '@/components/hook/useRealtimeMessaging'
import Logger from '@/util/logger/Logger'
import StoreUtil from '@/store/StoreUtil'

/**
 * マイページ 共通Wrapper
 */
export default defineComponent({
  name: 'MyPageWrapper',
  components: {
    HeaderPane,
  },
  props: {
    /**
     * ヘッダーナビ表示指定
     */
    hasHeaderNav: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    // store
    const notificationStore = StoreUtil.useStore('NotificationStore')

    // hook
    const { fetchNotificationData } = notificationStore
    const { initRTM, subscribeRTM, unSubscribeRTM } = useRealtimeMessaging()
    const { redirectPath } = usePusherContractInfo()

    const router = inject('router') as VueRouter

    onBeforeMount(async () => {
      // お知らせのリアルタイムメッセージを受信
      await initRTM()
      subscribeRTM('information', async (data: RTMCallbackParamType) => {
        Logger.debug(
          `MyPageWrapper#subscribeRTM: Receive notification event. event: ${JSON.stringify(data)}`,
        )
        fetchNotificationData()
      })
    })

    onUnmounted(() => {
      // お知らせに関連するリアルタイムメッセージ受信を停止
      unSubscribeRTM()
    })

    const pathListNoRedirectToUserEdit = [
      '/mypage/one-time-pass-confirm',
      '/mypage/one-time-pass-complete',
    ]

    /**
     * 以下の条件に当てはまった場合はリダイレクトする
     * 1. 契約情報取得に失敗した場合は、強制ログアウトする（ユーザー操作で再度ログインしてもらう）
     * 2. 有料機能が使えるようになったタイミングで郵便番号が入っていない場合、マイページの会員情報編集画面に遷移する
     */
    watch(
      () => redirectPath.value,
      () => {
        if (redirectPath.value) {
          if (redirectPath.value === '/mypage/user-edit') {
            if (pathListNoRedirectToUserEdit.some((path) => path === router.currentRoute.path)) {
              // 有料にアップグレード手続きをしている場合は完了画面を表示させるため、強制的にリダイレクトしない
              return
            }
          }
          router.replace({ path: redirectPath.value })
        }
      },
    )
  },
})
</script>

<style lang="scss" scoped>
.mypage-wrapper {
  background: $gray1;
  min-height: var(--screen-height);
  padding-bottom: 0;
}
</style>

<style lang="scss">
.page-wrapper .mypage-wrapper {
  .mypage-top-pane,
  .mypage-user-edit-pane,
  .mypage-user-confirm-pane,
  .mypage-user-complete-pane,
  .mypage-language-setting-pane,
  .mypage-details-setting-pane,
  .mypage-about-using-pane,
  .mypage-favorite-player-select-pane,
  .mypage-favorite-team-select-pane,
  .mypage-mail-change-complete-pane {
    height: auto;
    max-width: 800px;
    min-height: calc(100vh - #{$headerPaneHeight} - #{$subHeaderPaneHeight});
    padding-bottom: 136px;
    background: none;
  }

  .mypage-top-pane {
    min-height: calc(100vh - #{$headerPaneHeight});
  }

  .membership-card-pane__content {
    max-width: 800px;
  }

  .mypage-favorite-player-link-section__content,
  .mypage-favorite-team-link-section__content,
  .mypage-link-list-parts,
  .details-data-list-parts {
    border-left: none;
    border-right: none;
  }

  .mypage-favorite-player-link-section__link,
  .mypage-favorite-team-link-section__link {
    max-width: 100%;
  }

  .select-list-section {
    padding-left: 0;
    padding-right: 0;
  }

  @media screen and (max-width: $responsiveTabletWidth) {
    .member-badge-list-parts__carousel {
      display: none;
    }
  }

  @media screen and (min-width: $responsiveTabletWidth + 1px) {
    .member-badge-list-parts__content {
      overflow: hidden;
    }
  }
}
</style>
